import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ProgressBar, Text } from "components";
import {
  selectGameId,
  selectGameObject,
} from "features/gamestate/gameStateSlice";
import { selectQuestionEntities } from "features/question/questionSlice";
import { getDisplayStatus, getRoundKeys, PUBLISH_STATUS } from "../lifecycle";
import "./RoundsCard.css";

// A clue counts as "written" only when its question actually has content —
// non-empty clue text AND at least one answer. Mirrors the server's clueStats
// definition (functions/src/utils/clueCompletion.js) so the Rounds copy agrees
// with the hero's clueStats-derived total (a cell can reference an empty
// question, which is assigned but not yet written).
const hasContent = (q) =>
  !!q &&
  typeof q.clue === "string" &&
  q.clue.trim().length > 0 &&
  Array.isArray(q.answers) &&
  q.answers.length > 0;

const computeWritten = (game, questions, roundKey) => {
  const cats = Array.isArray(game?.categories?.[roundKey])
    ? game.categories[roundKey]
    : [];
  let written = 0;
  let total = 0;
  for (const cat of cats) {
    for (let cell = 1; cell <= 5; cell += 1) {
      total += 1;
      const qid = cat && cat[String(cell)];
      if (qid && hasContent(questions?.[qid])) written += 1;
    }
  }
  return { written, total };
};

// Walk the round's category map to enumerate question IDs, then look up each
// entity. Question entities don't carry a `round` field, so we can't filter by
// round on the entities. "Played" = the authoritative `picked` flag the
// selectQuestion transaction sets — the same signal that drives cluesPlayed.
const computeAnswered = (game, questions, roundKey) => {
  // Final-round categories can be a single object rather than an array; guard
  // so a non-array doesn't crash the for…of below.
  const cats = Array.isArray(game?.categories?.[roundKey])
    ? game.categories[roundKey]
    : [];
  let answered = 0;
  let total = 0;
  for (const cat of cats) {
    for (let cell = 1; cell <= 5; cell += 1) {
      const qid = cat && cat[String(cell)];
      if (!qid) continue;
      total += 1;
      if (questions?.[qid]?.picked) answered += 1;
    }
  }
  return { answered, total };
};

const Chevron = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

const RoundRow = ({ label, value, total, statusMode, onClick }) => {
  const pct = total > 0 ? (value / total) * 100 : 0;
  let tone = "accent";
  if (statusMode === "played") tone = "gold";
  else if (pct >= 100) tone = "gold";
  let copy;
  if (statusMode === "in-progress") copy = `${value} / ${total} clues answered`;
  else if (statusMode === "played") copy = `Complete · ${value} clues played`;
  else copy = `${value} / ${total} clues written`;

  return (
    <button className="gx-rc-row" onClick={onClick}>
      <div className="gx-rc-row-head">
        <span className="gx-rc-row-label">{label}</span>
        <span className="gx-rc-row-count">{copy}</span>
        <Chevron />
      </div>
      <ProgressBar value={pct} tone={tone} height={6} />
    </button>
  );
};

const RoundsCard = () => {
  const game = useSelector(selectGameObject);
  const gameId = useSelector(selectGameId);
  const questions = useSelector(selectQuestionEntities);
  const navigate = useNavigate();

  if (!game) return null;
  const display = getDisplayStatus(game);
  const isInProgress = display === PUBLISH_STATUS.IN_PROGRESS;
  const isPlayed = display === PUBLISH_STATUS.PLAYED;
  const statusMode = isInProgress ? "in-progress" : isPlayed ? "played" : "draft";

  const rounds = getRoundKeys(game);
  const finalKey = "final";

  return (
    <div className="gx-rc">
      <Text variant="h2" className="gx-rc-title">Rounds</Text>
      <div className="gx-rc-list">
        {rounds.map((roundKey) => {
          const numLabel = `Round ${roundKey.slice(5)}`;
          if (isInProgress) {
            const { answered, total } = computeAnswered(game, questions, roundKey);
            return (
              <RoundRow
                key={roundKey}
                label={numLabel}
                value={answered}
                total={total}
                statusMode="in-progress"
                onClick={() => navigate(`/manage/game/${gameId}/editor/${roundKey}`)}
              />
            );
          }
          if (isPlayed) {
            const { answered, total } = computeAnswered(game, questions, roundKey);
            return (
              <RoundRow
                key={roundKey}
                label={numLabel}
                value={answered || total}
                total={total}
                statusMode="played"
                onClick={() => navigate(`/manage/game/${gameId}/editor/${roundKey}`)}
              />
            );
          }
          const { written, total } = computeWritten(game, questions, roundKey);
          return (
            <RoundRow
              key={roundKey}
              label={numLabel}
              value={written}
              total={total}
              statusMode={statusMode}
              onClick={() => navigate(`/manage/game/${gameId}/editor/${roundKey}`)}
            />
          );
        })}
        {/* Final round — always present in the list */}
        {(() => {
          if (isInProgress || isPlayed) {
            const { answered, total } = computeAnswered(game, questions, finalKey);
            return (
              <RoundRow
                key="final"
                label="Final Round"
                value={isPlayed ? (answered || total) : answered}
                total={total || 1}
                statusMode={isPlayed ? "played" : "in-progress"}
                onClick={() => navigate(`/manage/game/${gameId}/editor/final`)}
              />
            );
          }
          // Final round is shaped { category, question: <questionId> } — not the
          // 1..5 cell map the other rounds use.
          const finalQid = game?.categories?.[finalKey]?.question;
          const written = finalQid && hasContent(questions?.[finalQid]) ? 1 : 0;
          return (
            <RoundRow
              key="final"
              label="Final Round"
              value={written}
              total={1}
              statusMode="draft"
              onClick={() => navigate(`/manage/game/${gameId}/editor/final`)}
            />
          );
        })()}
      </div>
    </div>
  );
};

export default RoundsCard;
